import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clientService, { Client, ClientListResponse } from '../../services/clientService';
import { useClientLimits } from '../../hooks/useClientLimits';
import { motion } from 'framer-motion';
import { Plus, Search, Users, ArrowLeft, ArrowRight, ChevronRight, Star, Building, Paintbrush, Crown, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const { canCreateClient, isUnlimited, currentCount, maxLimit, remaining, isBetaTester } = useClientLimits();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Show create button if limit is not 0 (even if at limit, show disabled button with upgrade prompt)
  const showCreateButton = maxLimit !== 0;

  useEffect(() => {
    loadClients();
  }, [currentPage, searchTerm]);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response: ClientListResponse = await clientService.getClients(
        currentPage,
        6, // 3x2 grid
        searchTerm || undefined
      );
      
      setClients(response.clients);
      setTotalPages(Math.ceil(response.total / response.per_page));
      setHasNext(response.has_next);
      setHasPrev(response.has_prev);
    } catch (err: any) {
      setError('Failed to load clients');
      console.error('Failed to load clients:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadClients();
  };

    const getRoleBadge = (role: string) => {
    const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-xs font-bold capitalize';
    switch (role) {
      case 'owner':
        return `${baseClasses} bg-purple-500/10 text-purple-500 dark:text-purple-400 border border-purple-500/20`;
      case 'admin':
        return `${baseClasses} bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20`;
      case 'editor':
        return `${baseClasses} bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20`;
      case 'member':
        return `${baseClasses} bg-green-500/10 text-green-500 dark:text-green-400 border border-green-500/20`;
      default:
        return `${baseClasses} bg-slate-500/10 text-slate-500 dark:text-slate-400 border border-slate-500/20`;
    }
  };

  const handleCreateClick = () => {
    if (canCreateClient) {
      navigate('/app/clients/new');
    } else {
      setShowUpgradeModal(true);
    }
  };

  return (
    <div className="min-h-screen text-slate-900 dark:text-white">
      {/* Floating Header */}
      <div className="sticky top-0 backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/30 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-6">
          <div className="flex items-center justify-between">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-indigo-500 via-blue-500 to-slate-600 dark:from-indigo-400 dark:via-blue-400 dark:to-slate-500 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-black bg-gradient-to-r from-slate-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200">
                    My Brand
                  </h1>
                  <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base lg:text-lg hidden sm:block">Manage your brand identity</p>
                </div>
              </div>
            </motion.div>
            {showCreateButton && (
              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                <div className="flex flex-col items-end gap-1">
                  {canCreateClient ? (
                    <motion.button 
                      onClick={handleCreateClick}
                      className="relative overflow-hidden flex items-center space-x-1 sm:space-x-2 px-3 py-2 sm:px-4 sm:py-2.5 lg:px-5 lg:py-3 text-white font-bold rounded-lg sm:rounded-xl shadow-lg transition-all duration-300"
                      style={{ 
                        background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                        backgroundSize: '200% 200%',
                        backgroundPosition: 'left center',
                        boxShadow: '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundPosition = 'right center';
                        e.currentTarget.style.boxShadow = '0 15px 35px rgba(48, 179, 221, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundPosition = 'left center';
                        e.currentTarget.style.boxShadow = '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      }}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="text-sm sm:text-base">Create</span>
                      <span className="hidden sm:inline">Brand</span>
                    </motion.button>
                  ) : (
                    <motion.button 
                      onClick={handleCreateClick}
                      className="relative overflow-hidden flex items-center space-x-1 sm:space-x-2 px-3 py-2 sm:px-4 sm:py-2.5 lg:px-5 lg:py-3 text-white font-bold rounded-lg sm:rounded-xl shadow-lg transition-all duration-300 cursor-pointer"
                      style={{ 
                        background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                        backgroundSize: '200% 200%',
                        backgroundPosition: 'left center',
                        boxShadow: '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                      }}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
                      <span className="text-sm sm:text-base">Create</span>
                      <span className="hidden sm:inline">Brand</span>
                    </motion.button>
                  )}
                  {!isUnlimited && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      {isBetaTester ? (
                        <span className="flex items-center gap-1">
                          <Crown className="w-3 h-3 text-yellow-500" />
                          Beta: Unlimited
                        </span>
                      ) : (
                        `${currentCount}/${maxLimit} brands`
                      )}
                    </span>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Brand Limit Reached
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-4">
                <p>
                  You've reached your limit of <span className="font-bold text-foreground">{maxLimit} brand{maxLimit !== 1 ? 's' : ''}</span>.
                </p>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Current usage:</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-foreground">{currentCount}/{maxLimit}</span>
                    <span className="text-sm text-muted-foreground">brands used</span>
                  </div>
                </div>
                <p className="text-sm">
                  Upgrade your plan to create more brands and unlock additional features.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/pricing');
              }}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              Upgrade Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Search Bar */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
          <form onSubmit={handleSearch} className="relative mb-4 sm:mb-6 lg:mb-8">
            <input
              type="text"
              placeholder="Search brands..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-slate-100/40 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-300/30 dark:border-slate-600/30 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all duration-300 text-base sm:text-lg"
            />
            <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
              <Search className="w-5 h-5 sm:w-6 sm:h-6 text-slate-500 dark:text-slate-400" />
            </div>
          </form>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center items-center h-96">
            <div className="w-16 h-16 border-4 border-slate-300/30 dark:border-slate-600/30 border-t-indigo-500 rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-96 bg-red-50/40 dark:bg-slate-800/40 rounded-3xl border border-red-500/30">
            <p className="text-2xl font-bold text-red-500 dark:text-red-400 mb-4">Error Loading Clients</p>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
            <motion.button 
              onClick={loadClients} 
              className="flex items-center space-x-2 px-5 py-3 bg-red-500/20 text-red-500 dark:text-red-300 font-bold rounded-xl border border-red-500/30 hover:bg-red-500/30 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
            >
              <span>Try Again</span>
            </motion.button>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-24 bg-slate-100/40 dark:bg-slate-800/40 rounded-3xl border border-slate-300/30 dark:border-slate-600/30">
            <Building className="mx-auto h-16 w-16 text-slate-400 dark:text-slate-500" />
            <h3 className="mt-6 text-2xl font-bold text-slate-800 dark:text-slate-200">Setting up your brand...</h3>
            <p className="mt-2 text-lg text-slate-600 dark:text-slate-400">
              {searchTerm ? 'Try a different search term or clear your search.' : 'Your brand workspace is being prepared. This should only take a moment.'}
            </p>
          </div>
        ) : (
          <>
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            >
              {clients.map((client, index) => (
                <motion.div
                  key={client.id}
                  className="relative group"
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <div className="absolute -inset-px bg-gradient-to-r from-indigo-500/30 via-blue-500/30 to-slate-500/30 dark:from-indigo-400/40 dark:via-blue-400/40 dark:to-slate-400/40 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg"></div>
                  <div className="relative h-full bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-6 flex flex-col group-hover:bg-white/80 dark:group-hover:bg-slate-800/80 group-hover:border-slate-300/60 dark:group-hover:border-slate-600/60 transition-all duration-300">
                    <div className="flex-grow">
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 rounded-lg sm:rounded-xl flex items-center justify-center border border-slate-300/50 dark:border-slate-600/50 flex-shrink-0">
                            {client.logo_url ? (
                              <img src={client.logo_url} alt={client.name} className="w-full h-full rounded-lg sm:rounded-xl object-cover" />
                            ) : (
                              <span className="text-lg sm:text-2xl font-black text-indigo-500 dark:text-indigo-400">{client.name.charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 group-hover:text-black dark:group-hover:text-white transition-colors truncate">{client.name}</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">@{client.slug}</p>
                          </div>
                        </div>

                      </div>

                      <p className="text-slate-600 dark:text-slate-300 line-clamp-2 sm:line-clamp-3 mb-3 sm:mb-4 min-h-[40px] sm:min-h-[60px] text-sm">{client.description || 'No description provided.'}</p>
                    </div>

                    <div className="mt-auto pt-3 sm:pt-4 border-t border-slate-200/50 dark:border-slate-700/50 flex flex-col space-y-2">
                      <Link to={`/app/clients/${client.id}/brand-kit`}>
                        <motion.button
                          className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-white font-bold rounded-lg transition-all duration-200 text-sm relative overflow-hidden"
                          style={{ 
                            background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                            backgroundSize: '200% 200%',
                            backgroundPosition: 'left center',
                            boxShadow: '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundPosition = 'right center';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(48, 179, 221, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                            e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundPosition = 'left center';
                            e.currentTarget.style.boxShadow = '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <Paintbrush className="w-4 h-4" />
                          <span>Manage Brand Kit</span>
                        </motion.button>
                      </Link>
                      <Link to={`/app/clients/${client.id}`} className="flex items-center justify-center space-x-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                        <span>View Details</span>
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div 
                className="flex items-center justify-center mt-6 sm:mt-8 lg:mt-12 space-x-2 sm:space-x-4"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
              >
                <motion.button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!hasPrev}
                  className="flex items-center space-x-1 sm:space-x-2 px-3 py-2 sm:px-4 sm:py-2.5 lg:px-5 lg:py-3 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-lg sm:rounded-xl text-slate-600 dark:text-slate-300 font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100/80 dark:hover:bg-slate-700/80 transition-all duration-200 text-sm sm:text-base"
                  whileHover={{ scale: 1.05 }}
                >
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </motion.button>
                
                <div className="text-slate-500 dark:text-slate-400 font-medium text-sm sm:text-base px-2">
                  <span className="hidden sm:inline">Page </span>{currentPage}<span className="hidden sm:inline"> of {totalPages}</span>
                  <span className="sm:hidden">/{totalPages}</span>
                </div>

                <motion.button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNext}
                  className="flex items-center space-x-1 sm:space-x-2 px-3 py-2 sm:px-4 sm:py-2.5 lg:px-5 lg:py-3 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-lg sm:rounded-xl text-slate-600 dark:text-slate-300 font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100/80 dark:hover:bg-slate-700/80 transition-all duration-200 text-sm sm:text-base"
                  whileHover={{ scale: 1.05 }}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </motion.button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientList;